import { useEffect, useState } from 'react';

import { useWorkspacesPageDirectoryQuery } from '../../../generated';
import type { DirectoryMember, MemberCount } from '../types';
import { mapToDirectoryEntries, mapToMemberCount } from '../utils';

import type { DirectoryFilters } from './useWorkspaceDetail';

export interface UseWorkspaceDirectoryResult {
  /** The members on the page currently shown — not the whole directory. */
  members: DirectoryMember[];
  isLoading: boolean;
  error: Error | null;
  /** True when the workspace itself did not resolve, rather than being empty. */
  notFound: boolean;
  /**
   * How many members match the current filters, across every page — a floor
   * once the workspace is larger than the service counts. Null until a read
   * has come back.
   */
  totalCount: MemberCount | null;
  /** 1-based, for display. The read is cursored, so there is no page count. */
  pageNumber: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  /** Back to the start without stepping back through every page visited. */
  goToFirstPage: () => void;
  refetch: () => Promise<void>;
}

/**
 * The workspace's whole directory: who is in it, by name. The directory carries
 * identity alone, so it takes a search term and nothing that narrows by place or
 * role — those questions belong to the shared-locations read.
 */
export const useWorkspaceDirectory = (
  workspaceId: string,
  filters: DirectoryFilters = {},
  pageSize = 10
): UseWorkspaceDirectoryResult => {
  const { searchTerm } = filters;

  // The read is cursored and forward-only: it hands back a token for the next
  // page and no total. Remembering the token each page started from is what
  // makes going back possible; the last entry is the current page, and
  // `undefined` is the first.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);

  // A different question deserves a first page. Without this, paging forward
  // then filtering would ask for page four of a list that no longer has one.
  const asked = JSON.stringify([
    workspaceId,
    searchTerm?.trim() ?? '',
    pageSize,
  ]);
  useEffect(() => {
    setCursors([undefined]);
  }, [asked]);

  const directory = useWorkspacesPageDirectoryQuery({
    variables: {
      input: {
        workspaceId,
        limit: pageSize,
        ...(cursors[cursors.length - 1]
          ? { cursor: cursors[cursors.length - 1] }
          : {}),
        // An empty control means unfiltered, not "match nothing".
        ...(searchTerm?.trim() ? { searchTerm: searchTerm.trim() } : {}),
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // New variables are a cache miss, so `data` is undefined until the read
  // returns. Falling back to the last result keeps the rows on screen while a
  // filter change is in flight rather than blanking the table.
  const result = (directory.data ?? directory.previousData)
    ?.teamDirectorySearch;

  // A workspace the caller cannot see is reported as not-found on purpose, so
  // one workspace never reveals another.
  const notFound = result?.__typename === 'WorkspaceNotFound';

  const resultError =
    result?.__typename === 'AuthorizationFailure' ||
    result?.__typename === 'InputValidationFailure'
      ? new Error(result.message ?? 'The directory could not be read.')
      : null;

  // Null once the last member has been returned, so its presence is the only
  // thing that says another page exists — there is no total to compare against.
  const nextCursor =
    result?.__typename === 'TeamDirectorySearchResult' ? result.cursor : null;

  return {
    members:
      result?.__typename === 'TeamDirectorySearchResult'
        ? mapToDirectoryEntries(result.entries)
        : [],
    totalCount:
      result?.__typename === 'TeamDirectorySearchResult'
        ? mapToMemberCount(result.totalCount)
        : null,
    isLoading: directory.loading,
    error: directory.error ?? resultError,
    notFound,
    pageNumber: cursors.length,
    hasNextPage: nextCursor !== null && nextCursor !== undefined,
    hasPreviousPage: cursors.length > 1,
    goToNextPage: () =>
      setCursors((visited) =>
        nextCursor ? [...visited, nextCursor] : visited
      ),
    goToPreviousPage: () =>
      setCursors((visited) =>
        visited.length > 1 ? visited.slice(0, -1) : visited
      ),
    // The first page is the one with no cursor, so returning to it is dropping
    // every token collected on the way out.
    goToFirstPage: () => setCursors([undefined]),
    refetch: async () => {
      await directory.refetch();
    },
  };
};
